import React, { useCallback, useEffect, useState } from "react";
import { ApiConstants } from "../../../main/constants/ApiConstants";
import { toast } from "react-toastify";
import IRoomType from "../../../main/interfaces/IRoomType";
import { useTranslation } from "react-i18next";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import GeneralModal from "../../../main/components/modal/general/GeneralModal";
import KingBedOutlinedIcon from "@mui/icons-material/KingBedOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import PublicBook from "./PublicBook";
import '../Hotel/Hotel.css';
import "./OnlineReservation.css";
import "./PublicExperience.css";
import axios from "axios";
import { loadEntityGalleryMap, getMetadataFileUrl, EntityFileMetadata, createPublicGalleryClient } from "../../../main/utils/entityGallery";
import { formatPublicPrice } from "./publicBookingCurrency";

const publicApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});
const publicGalleryApi = createPublicGalleryClient();

interface IRoomAvailability {
  roomId: number;
  roomNumber: string;
  roomTypeId: number;
  roomTypeName: string;
  pricePerNight?: number | null;
  totalPrice?: number | null;
  recommendedRatePlanId?: number | null;
  recommendedRatePlanName?: string | null;
  rateOptions?: Array<{
    roomRateId: number;
    ratePlanId: number;
    ratePlanName: string;
    pricePerNight: number;
    totalPrice: number;
    nightCount: number;
    isRefundable: boolean;
    validFrom: string;
    validTo: string;
  }>;
}

interface Props {
  hotelId: number | null;
  hotelName: string;
  hotelCheckInTime: string;
  hotelCheckOutTime: string;
  onDatesChange: (checkInDate: string, checkOutDate: string) => void;
  refreshKey: number;
  selectedCurrency: string;
  onRoomSelect?: (
    room: IRoomAvailability,
    checkInDate: string,
    checkOutDate: string,
    roomPhotos: EntityFileMetadata[]
  ) => void;
}

const normalizeRoomType = (roomType: any): IRoomType => ({
  ...roomType,
  id: Number(roomType?.id ?? roomType?.Id ?? 0),
  hotelId: Number(roomType?.hotelId ?? roomType?.HotelId ?? 0),
  name: roomType?.name ?? roomType?.Name ?? "",
  code: roomType?.code ?? roomType?.Code ?? "",
  baseOccupancy: Number(roomType?.baseOccupancy ?? roomType?.BaseOccupancy ?? 0),
  maxOccupancy: Number(roomType?.maxOccupancy ?? roomType?.MaxOccupancy ?? 0),
  defaultNightPrice: Number(roomType?.defaultNightPrice ?? roomType?.DefaultNightPrice ?? 0),
  description: roomType?.description ?? roomType?.Description ?? "",
});

const normalizeRoomAvailability = (room: any): IRoomAvailability => ({
  roomId: Number(room?.roomId ?? room?.RoomId ?? 0),
  roomNumber: room?.roomNumber ?? room?.RoomNumber ?? "",
  roomTypeId: Number(room?.roomTypeId ?? room?.RoomTypeId ?? 0),
  roomTypeName: room?.roomTypeName ?? room?.RoomTypeName ?? "",
  pricePerNight: room?.pricePerNight ?? room?.PricePerNight ?? null,
  totalPrice: room?.totalPrice ?? room?.TotalPrice ?? null,
  recommendedRatePlanId: room?.recommendedRatePlanId ?? room?.RecommendedRatePlanId ?? null,
  recommendedRatePlanName: room?.recommendedRatePlanName ?? room?.RecommendedRatePlanName ?? null,
  rateOptions: (room?.rateOptions ?? room?.RateOptions ?? []).map((option: any) => ({
    roomRateId: Number(option?.roomRateId ?? option?.RoomRateId ?? 0),
    ratePlanId: Number(option?.ratePlanId ?? option?.RatePlanId ?? 0),
    ratePlanName: option?.ratePlanName ?? option?.RatePlanName ?? "",
    pricePerNight: Number(option?.pricePerNight ?? option?.PricePerNight ?? 0),
    totalPrice: Number(option?.totalPrice ?? option?.TotalPrice ?? 0),
    nightCount: Number(option?.nightCount ?? option?.NightCount ?? 0),
    isRefundable: Boolean(option?.isRefundable ?? option?.IsRefundable),
    validFrom: option?.validFrom ?? option?.ValidFrom ?? "",
    validTo: option?.validTo ?? option?.ValidTo ?? "",
  })),
});

const PublicRoomAvailability = ({
  hotelId,
  hotelName,
  hotelCheckInTime,
  hotelCheckOutTime,
  onDatesChange,
  refreshKey,
  selectedCurrency,
  onRoomSelect,
}: Props) => {
  const { t, i18n } = useTranslation();
  const [rooms, setRooms] = useState<IRoomAvailability[]>([]);
  const [roomTypes, setRoomTypes] = useState<IRoomType[]>([]);
  const [selectedRoomTypeId, setSelectedRoomTypeId] = useState("");
  const [checkInDate, setCheckInDate] = useState("");
  const [checkOutDate, setCheckOutDate] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [roomPhotoMap, setRoomPhotoMap] = useState<Record<number, EntityFileMetadata[]>>({});
  const [selectedRoom, setSelectedRoom] = useState<{
    room: IRoomAvailability;
    checkInDate: string;
    checkOutDate: string;
    roomPhotos: EntityFileMetadata[];
  } | null>(null);
  const currentLanguage = (i18n.resolvedLanguage || i18n.language || "en").toLowerCase();

  useEffect(() => {
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    const format = (date: Date) => date.toISOString().split("T")[0];

    setCheckInDate(format(today));
    setCheckOutDate(format(tomorrow));
  }, []);

  useEffect(() => {
    setSelectedRoomTypeId("");
    setRoomTypes([]);
  }, [hotelId]);

  useEffect(() => {
    const loadRoomTypes = async () => {
      if (!hotelId) return;

      try {
        const res = await publicApi.get(ApiConstants.publicHotel.roomTypes, {
          params: { hotelId },
        });

        const payload = res.data?.roomTypes ?? res.data?.items ?? res.data ?? [];
        const data = Array.isArray(payload) ? payload.map(normalizeRoomType) : [];
        setRoomTypes(data);
      } catch (err: any) {
        console.error("ROOM TYPES ERROR:", err?.response?.data || err.message);
      }
    };

    void loadRoomTypes();
  }, [hotelId]);

  useEffect(() => {
    if (!selectedRoomTypeId) return;

    const hasSelectedRoomType = roomTypes.some((roomType) => Number(roomType.id) === Number(selectedRoomTypeId));
    if (!hasSelectedRoomType) {
      setSelectedRoomTypeId("");
    }
  }, [roomTypes, selectedRoomTypeId]);

  const searchRooms = useCallback(async () => {
    if (!hotelId) return;

    if (!checkInDate || !checkOutDate) {
      toast.warning(t("reservationDesk.selectDatesFirst"));
      return;
    }

    try {
      const res = await publicApi.post(
        ApiConstants.publicHotel.availability,
        {
          hotelId,
          checkInDate,
          checkOutDate,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: false,
        }
      );

      let data = res.data?.items || res.data || [];
      data = Array.isArray(data) ? data.map(normalizeRoomAvailability) : [];

      if (roomTypes.length === 0 && data.length > 0) {
        const derivedRoomTypesMap = new Map<number, IRoomType>(
          data
            .filter((room: IRoomAvailability) => room.roomTypeId > 0)
            .map((room: IRoomAvailability) => [
              room.roomTypeId,
              {
                id: room.roomTypeId,
                hotelId: hotelId ?? 0,
                name: room.roomTypeName || `Room Type ${room.roomTypeId}`,
                code: "",
                baseOccupancy: 0,
                maxOccupancy: 0,
                defaultNightPrice: 0,
                description: "",
              },
            ])
        );

        setRoomTypes(Array.from(derivedRoomTypesMap.values()));
      }

      if (selectedRoomTypeId && roomTypes.some((roomType) => Number(roomType.id) === Number(selectedRoomTypeId))) {
        data = data.filter((room: IRoomAvailability) => Number(room.roomTypeId) === Number(selectedRoomTypeId));
      }

      setRooms(data);
      const galleryMap = await loadEntityGalleryMap(
        data.map((room: IRoomAvailability) => room.roomId).filter(Boolean),
        ApiConstants.EntityTypes.Room,
        5,
        publicGalleryApi,
      );
      setRoomPhotoMap(galleryMap);
      setHasSearched(true);
      onDatesChange(checkInDate, checkOutDate);
    } catch (err: any) {
      console.error("AVAILABILITY ERROR:", err?.response?.data || err.message);
      toast.error(t("reservationDesk.failedToLoadAvailability"));
    }
  }, [checkInDate, checkOutDate, hotelId, onDatesChange, roomTypes, selectedRoomTypeId, t]);

  useEffect(() => {
    if (hotelId) {
      void searchRooms();
    }
  }, [hotelId, refreshKey, searchRooms, selectedRoomTypeId]);

  return (
    <div className="or-room-directory">
      <section className="or-hotels-shell or-hotels-hero or-room-hero">
        <div className="or-hotels-hero-layout">
          <div className="or-hotels-hero-copy">
            <div className="or-hotels-hero-topline">
              <span className="or-hotels-hero-badge">
                <CalendarMonthOutlinedIcon fontSize="inherit" />
                {t("publicBooking.liveAvailability", { defaultValue: "Live availability" })}
              </span>
            </div>

            <h1 className="or-hotels-hero-title">
              {hotelName}
              <br />
              <span>{t("reservationDesk.searchRooms", { defaultValue: "Search rooms" })}</span>
            </h1>

            <p className="or-hotels-hero-text">
              {t("publicBooking.searchIntro", {
                defaultValue: "Choose dates, compare room options, and send a booking request with the same public theme experience."
              })}
            </p>
          </div>

          <div className="or-hotels-hero-stats">
            <div className="or-hotels-hero-stat">
              <span className="or-hotels-stat-icon">{hotelCheckInTime}</span>
              <strong>{hotelCheckInTime}</strong>
              <span>{t("reservationDesk.checkIn")}</span>
            </div>
            <div className="or-hotels-hero-stat">
              <span className="or-hotels-stat-icon">{hotelCheckOutTime}</span>
              <strong>{hotelCheckOutTime}</strong>
              <span>{t("reservationDesk.checkOut")}</span>
            </div>
            <div className="or-hotels-hero-stat">
              <span className="or-hotels-stat-icon">
                <EventAvailableOutlinedIcon fontSize="inherit" />
              </span>
              <strong>{rooms.length}</strong>
              <span>{t("publicBooking.freeRooms", { defaultValue: "Free rooms" })}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="or-hotels-shell or-hotels-filters">
        <div className="or-room-filter-grid">
          <div className="or-room-filter-card">
            <label>{t("reservationDesk.checkIn")}</label>
            <input type="date" value={checkInDate} onChange={(e) => setCheckInDate(e.target.value)} />
          </div>

          <div className="or-room-filter-card">
            <label>{t("reservationDesk.checkOut")}</label>
            <input type="date" value={checkOutDate} onChange={(e) => setCheckOutDate(e.target.value)} />
          </div>

          <div className="or-room-filter-card">
            <label>{t("reservationDesk.roomType")}</label>
            <select value={selectedRoomTypeId} onChange={(e) => setSelectedRoomTypeId(e.target.value)}>
              <option value="">{t("reservationDesk.allRoomTypes")}</option>
              {roomTypes.map((roomType) => (
                <option key={roomType.id} value={roomType.id}>
                  {roomType.name}
                </option>
              ))}
            </select>
          </div>

          <div className="or-room-filter-cta">
            <button className="or-search-btn or-search-cta" onClick={searchRooms} type="button">
              {t("reservationDesk.searchRooms")}
            </button>
          </div>
        </div>

        <div className="or-hotels-results-line">
          <strong>{rooms.length}</strong> {t("publicBooking.freeRooms", { defaultValue: "Free rooms" })} • <strong>{roomTypes.length || 0}</strong>{" "}
          {t("publicBooking.activeTypes", { defaultValue: "Active types" })}
        </div>
      </section>

      {hasSearched && rooms.length === 0 ? (
        <div className="or-hotels-shell or-hotels-empty">
          <strong>{t("reservationDesk.noAvailableRooms")}</strong>
          <span>{t("publicBooking.tryOtherDates")}</span>
        </div>
      ) : null}

      <div className="or-hotels-shell or-room-grid">
        {rooms.map((room) => {
          const firstPhotoUrl = getMetadataFileUrl(roomPhotoMap[room.roomId]?.[0]?.id);

          return (
            <article key={room.roomId} className="or-room-card">
              <div
                className={`or-room-card-image ${firstPhotoUrl ? "has-photo" : ""}`}
                style={firstPhotoUrl ? { ["--or-card-photo" as any]: `url("${firstPhotoUrl}")` } : undefined}
              >
                <div className="or-room-card-overlay">
                  <span className="or-hotels-online-badge">{t("generic.available")}</span>
                  <div className="or-room-card-overlay-copy">
                    <span className="or-hotels-card-overline">{room.roomTypeName}</span>
                    <h3>{t("reservationDesk.roomNumber", { roomNumber: room.roomNumber })}</h3>
                  </div>
                </div>
              </div>

              <div className="or-room-card-bottom">
                <div className="or-hotels-location">
                  <MeetingRoomOutlinedIcon fontSize="inherit" />
                  {room.roomTypeName}
                </div>

                <div className="or-hotels-room-tags">
                  {room.pricePerNight ? (
                    <span className="or-hotels-tag">
                      <LocalOfferOutlinedIcon fontSize="inherit" />
                      {formatPublicPrice(room.pricePerNight, selectedCurrency, currentLanguage)} / {t("reservationDesk.perNight")}
                    </span>
                  ) : null}
                  {room.totalPrice ? (
                    <span className="or-hotels-tag">
                      <KingBedOutlinedIcon fontSize="inherit" />
                      {t("generic.total")}: {formatPublicPrice(room.totalPrice, selectedCurrency, currentLanguage)}
                    </span>
                  ) : null}
                </div>

                <button
                  className="or-reserve-btn"
                  type="button"
                  onClick={() => {
                    const roomPhotos = roomPhotoMap[room.roomId] || [];
                    if (onRoomSelect) {
                      onRoomSelect(room, checkInDate, checkOutDate, roomPhotos);
                      return;
                    }

                    setSelectedRoom({
                      room,
                      checkInDate,
                      checkOutDate,
                      roomPhotos,
                    });
                  }}
                >
                  {t("reservationDesk.requestBooking")}
                </button>
              </div>
            </article>
          );
        })}
      </div>

      {selectedRoom ? (
        <GeneralModal
          handleClose={() => setSelectedRoom(null)}
          headerName={t("reservationDesk.sendBookingRequest")}
          width="780px"
          radius="18px"
          hideFooter
        >
          <PublicBook
            hotelId={hotelId}
            room={selectedRoom.room}
            checkInDate={selectedRoom.checkInDate}
            checkOutDate={selectedRoom.checkOutDate}
            roomPhotos={selectedRoom.roomPhotos || []}
            selectedCurrency={selectedCurrency}
            onClose={() => setSelectedRoom(null)}
          />
        </GeneralModal>
      ) : null}
    </div>
  );
};

export default PublicRoomAvailability;
